import React, { useState } from 'react';
import { Modal, List, Avatar, Typography, Space, Spin, Button, Card, Radio, Tag, Tooltip } from 'antd';
import { GiftOutlined, StarFilled, FireOutlined, LoadingOutlined, CheckCircleFilled, TrophyOutlined, LockOutlined } from '@ant-design/icons';
import { getJavamonImage } from '../../../../dao/jutge/GetJavamonConfig';

const { Text, Title, Paragraph } = Typography;

export interface RewardItem {
    id?: number | string;
    tipo: string;
    valor?: number;
    data?: any;
    id_item?: string;
    is_unique?: boolean;
    limit_quantity?: number;
    claimed_count?: number;
    intentos_min?: number;
    intentos_max?: number;
    time_limit?: number;
    nota_minima?: number;
    grupo_seleccion?: number;
    nom?: string;
    name?: string;
}

export interface RecompensaSummaryModalProps {
    visible: boolean;
    onClose: () => void;
    rewards: RewardItem[];
    loading?: boolean;
    status?: string; // 'ok' | 'empty'
    emptyReason?: string; // 'RECLAMADA' | 'AGOTADA'
    message?: string;
    // Props for selectable multi-choice rewards mode
    selectableMode?: boolean;
    selectionOptions?: RewardItem[];
    guaranteedRewards?: RewardItem[];
    onConfirmSelection?: (selectedItem: RewardItem) => void;
    currentAttempts?: number;
    currentNota?: number;
    currentTimeSeconds?: number;
}

const RecompensaSummaryModal: React.FC<RecompensaSummaryModalProps> = ({
    visible,
    onClose,
    rewards,
    loading = false,
    status = 'ok',
    emptyReason,
    message: customMessage,
    selectableMode = false,
    selectionOptions = [],
    guaranteedRewards = [],
    onConfirmSelection,
    currentAttempts,
    currentNota,
    currentTimeSeconds
}) => {
    const [selectedId, setSelectedId] = useState<number | string | null>(null);

    const isItemReward = (tipo: string) => {
        return ['JAVAMON', 'SKIN', 'ARMA', 'ESCUDO', 'ARCO', 'FLECHA', 'MAGIA', 'AVATAR', 'SHOP_ITEM'].includes(tipo);
    };

    const hasUniqueItem = rewards.some((r) => r.is_unique);

    const getRewardImageUrl = (item: RewardItem) => {
        const urlBase = process.env.REACT_APP_API_URL;
        if (item.tipo === 'JAVAMON' || item.tipo === 'JAVAMON_RANDOM' || item.tipo === 'JAVAMON_SEASON') {
            if (item.data) return getJavamonImage(item.data);
            if (item.id_item) return `${urlBase}:8061/jutge/game/image/javamon/${item.id_item}`;
            return null;
        }
        if (isItemReward(item.tipo)) {
            const segByTipo: Record<string, string> = {
                SHOP_ITEM: 'shop',
                SKIN: 'skin',
                ARMA: 'arma',
                ESCUDO: 'escut',
                ARCO: 'arc',
                FLECHA: 'flecha',
                MAGIA: 'magia',
                AVATAR: 'avatar',
                JAVAMON: 'javamon',
            };
            const seg = segByTipo[item.tipo] || item.tipo.toLowerCase();
            const jm = item.data || { id: item.id_item, _type: seg };
            return getJavamonImage(jm);
        }
        return null;
    };

    const getIconForReward = (item: RewardItem) => {
        if (item.tipo === 'PUNTUACION') {
            return <Avatar style={{ backgroundColor: '#FFD700' }} icon={<StarFilled style={{ color: '#fff' }} />} />;
        }
        if (item.tipo === 'JAVAMON' || item.tipo === 'JAVAMON_RANDOM' || item.tipo === 'JAVAMON_SEASON') {
            const imgUrl = getRewardImageUrl(item);
            if (imgUrl) {
                return <img src={imgUrl} style={{ width: 40, height: 40, objectFit: 'contain' }} alt="javamon" />;
            }
            return <Avatar style={{ backgroundColor: '#722ed1' }} icon={<span style={{ fontSize: 24, fontWeight: 'bold', color: 'white' }}>?</span>} size="large" />;
        }
        if (isItemReward(item.tipo)) {
            const imgUrl = getRewardImageUrl(item);
            return <img src={imgUrl || ''} style={{ width: 40, height: 40, objectFit: 'contain' }} alt="reward"
                        onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />;
        }
        return <Avatar icon={<GiftOutlined />} />;
    };

    const getDescriptionForReward = (item: RewardItem) => {
        const uniqueBadge = item.is_unique ? (
            <div style={{ marginTop: 4 }}>
                <span style={{ 
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'linear-gradient(135deg, #ffd700, #ff8c00)',
                    color: '#fff',
                    padding: '2px 8px',
                    borderRadius: 10,
                    fontSize: 11,
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(255,140,0,0.3)'
                }}>
                    ✨ ¡Objeto Único!
                </span>
            </div>
        ) : null;

        if (item.tipo === 'PUNTUACION') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong style={{ color: '#faad14' }}>+{item.valor} Puntos</Text>
                    {uniqueBadge}
                </div>
            );
        }
        if (item.tipo === 'JAVAMON' || item.tipo === 'JAVAMON_RANDOM' || item.tipo === 'JAVAMON_SEASON') {
            const jName = item.data?.nom || item.data?.name || (item.tipo === 'JAVAMON_RANDOM' ? 'Javamon Aleatorio' : 'Javamon Especial');
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong>¡{jName}!</Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Cierra esta ventana para intentar capturarlo.</Text>
                    {uniqueBadge}
                </div>
            );
        }
        if (isItemReward(item.tipo)) {
            const name = item.data?.nom || item.data?.name || item.nom || item.name || (item.tipo.charAt(0) + item.tipo.slice(1).toLowerCase());
            return (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong>{item.tipo}: {name}</Text>
                    {uniqueBadge}
                </div>
            );
        }
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>Recompensa Misteriosa</Text>
                {uniqueBadge}
            </div>
        );
    };

    const isEmpty = status === 'empty';
    const isReclamada = emptyReason === 'RECLAMADA' || (customMessage && customMessage.toLowerCase().includes('reclamad'));
    const emptyTitle = isReclamada ? "Recompensa Ya Reclamada" : "Recompensa Agotada";
    const emptyIcon = isReclamada ? "⚠️" : "⏳";
    const defaultEmptyDesc = isReclamada
        ? "Ya has reclamado anteriormente esta recompensa en este botín."
        : "Todas las unidades disponibles de esta recompensa ya han sido reclamadas por otros usuarios.";

    const handleConfirm = () => {
        if (!selectedId) return;
        const chosen = selectionOptions.find(o => String(o.id) === String(selectedId));
        if (chosen && onConfirmSelection) {
            onConfirmSelection(chosen);
        }
    };

    // Width dynamically adapts: wider for selectable cards grid
    const modalWidth = selectableMode ? Math.min(820, Math.max(520, selectionOptions.length * 240)) : 420;

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            closable={!loading}
            maskClosable={!loading && !selectableMode}
            keyboard={!loading}
            footer={null}
            centered
            zIndex={100000}
            bodyStyle={{ padding: selectableMode ? '28px 32px' : '24px', textAlign: 'center' }}
            width={modalWidth}
        >
            {selectableMode ? (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <TrophyOutlined style={{ fontSize: 50, color: '#faad14', marginBottom: 12 }} />
                        <Title level={3} style={{ margin: 0, color: '#1f1f1f' }}>
                            ¡Elige tu Recompensa!
                        </Title>
                        <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 14 }}>
                            Has superado el desafío. Elige <strong>UNA</strong> opción entre las siguientes alternativas:
                        </Paragraph>

                        {guaranteedRewards && guaranteedRewards.length > 0 && (
                            <div style={{
                                marginTop: 12,
                                padding: '10px 16px',
                                background: '#f6ffed',
                                border: '1px solid #b7eb8f',
                                borderRadius: 10,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                <Text strong style={{ color: '#389e0d', fontSize: 13 }}>
                                    🎁 Recompensas adicionales garantizadas:
                                </Text>
                                {guaranteedRewards.map((gr, idx) => {
                                    const grName = gr.data?.nom || gr.data?.name || gr.nom || gr.name || (gr.tipo === 'PUNTUACION' ? `+${gr.valor} Pts` : gr.tipo);
                                    return (
                                        <Tag key={gr.id || idx} color="green" style={{ margin: 0, fontWeight: 600 }}>
                                            {grName}
                                        </Tag>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fit, minmax(210px, 1fr))`,
                        gap: 16,
                        margin: '24px 0',
                        maxHeight: '55vh',
                        overflowY: 'auto',
                        padding: '4px'
                    }}>
                        {selectionOptions.map((opt) => {
                            const isSelected = String(selectedId) === String(opt.id);
                            const imgUrl = getRewardImageUrl(opt);
                            const itemName = opt.data?.nom || opt.data?.name || opt.nom || opt.name || (opt.tipo === 'PUNTUACION' ? `+${opt.valor} Puntos` : opt.tipo);

                            // Validar restricciones
                            let isRestricted = false;
                            let restrictionReason = '';

                            const limit = Number(opt.limit_quantity);
                            const claimed = Number(opt.claimed_count || 0);
                            if (limit > 0 && claimed >= limit) {
                                isRestricted = true;
                                restrictionReason = 'Agotada (Sense existències)';
                            } else if (opt.intentos_min && currentAttempts !== undefined && currentAttempts > 0 && currentAttempts < opt.intentos_min) {
                                isRestricted = true;
                                restrictionReason = `Requereix mínim ${opt.intentos_min} intents (en portes ${currentAttempts})`;
                            } else if (opt.intentos_max && currentAttempts !== undefined && currentAttempts > opt.intentos_max) {
                                isRestricted = true;
                                restrictionReason = `Màxim ${opt.intentos_max} intents superat (en portes ${currentAttempts})`;
                            } else if (opt.time_limit && currentTimeSeconds !== undefined && currentTimeSeconds > (opt.time_limit * 60)) {
                                isRestricted = true;
                                const elapsedMin = Math.round(currentTimeSeconds / 60);
                                restrictionReason = `Temps límit de ${opt.time_limit}m superat (${elapsedMin}m)`;
                            } else if (opt.nota_minima && currentNota !== undefined && currentNota < opt.nota_minima) {
                                isRestricted = true;
                                restrictionReason = `Requereix nota mínima de ${opt.nota_minima}% (tens ${currentNota}%)`;
                            }

                            return (
                                <Tooltip key={opt.id} title={isRestricted ? restrictionReason : undefined}>
                                    <div
                                        onClick={() => {
                                            if (!isRestricted) {
                                                setSelectedId(opt.id ?? null);
                                            }
                                        }}
                                        style={{
                                            border: isSelected ? '2px solid #1890ff' : '2px solid #e8e8e8',
                                            borderRadius: 16,
                                            padding: '20px 16px',
                                            cursor: isRestricted ? 'not-allowed' : 'pointer',
                                            background: isRestricted
                                                ? '#f5f5f5'
                                                : isSelected 
                                                    ? 'linear-gradient(180deg, #e6f7ff 0%, #ffffff 100%)' 
                                                    : '#fafafa',
                                            boxShadow: isSelected ? '0 8px 20px rgba(24, 144, 255, 0.25)' : '0 2px 8px rgba(0,0,0,0.04)',
                                            transition: 'all 0.25s ease',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            position: 'relative',
                                            opacity: isRestricted ? 0.6 : 1,
                                            filter: isRestricted ? 'grayscale(80%)' : 'none'
                                        }}
                                    >
                                        {isSelected && !isRestricted && (
                                            <div style={{ position: 'absolute', top: 10, right: 10 }}>
                                                <CheckCircleFilled style={{ fontSize: 20, color: '#1890ff' }} />
                                            </div>
                                        )}

                                        {isRestricted && (
                                            <div style={{ position: 'absolute', top: 8, right: 8 }}>
                                                <Tag color="error" style={{ margin: 0, fontSize: 10, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LockOutlined /> Bloquejada
                                                </Tag>
                                            </div>
                                        )}

                                        <div style={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: 12,
                                            background: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginBottom: 14,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                                        }}>
                                            {opt.tipo === 'PUNTUACION' ? (
                                                <StarFilled style={{ fontSize: 44, color: '#FFD700' }} />
                                            ) : imgUrl ? (
                                                <img
                                                    src={imgUrl}
                                                    alt={itemName}
                                                    style={{ width: 68, height: 68, objectFit: 'contain' }}
                                                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                                                />
                                            ) : (
                                                <GiftOutlined style={{ fontSize: 40, color: '#722ed1' }} />
                                            )}
                                        </div>

                                        <Text strong style={{ fontSize: 15, marginBottom: 4, textAlign: 'center', color: isSelected ? '#096dd9' : '#262626' }}>
                                            {itemName}
                                        </Text>
                                        <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            {opt.tipo}
                                        </Text>

                                        {isRestricted ? (
                                            <div style={{ marginTop: 10, fontSize: 11, color: '#cf1322', textAlign: 'center', fontWeight: 500 }}>
                                                {restrictionReason}
                                            </div>
                                        ) : (
                                            <div style={{ marginTop: 14 }}>
                                                <Radio checked={isSelected} />
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                            );
                        })}
                    </div>

                    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 12 }}>
                        <Button size="large" onClick={onClose} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            disabled={!selectedId}
                            loading={loading}
                            onClick={handleConfirm}
                            style={{
                                minWidth: 160,
                                background: selectedId ? 'linear-gradient(90deg, #1890ff, #096dd9)' : undefined,
                                fontWeight: 600
                            }}
                        >
                            Confirmar y Reclamar
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <div style={{ marginBottom: 20 }}>
                        {isEmpty ? (
                            <div style={{ fontSize: 52, marginBottom: 12 }}>{emptyIcon}</div>
                        ) : (
                            <GiftOutlined style={{ fontSize: 48, color: '#f5222d', marginBottom: 16 }} className="animate-bounce" />
                        )}
                        
                        <Title level={3} style={{ marginTop: 0 }}>
                            {isEmpty ? emptyTitle : "¡Recompensas Obtenidas!"}
                        </Title>

                        {isEmpty ? (
                            <div style={{ padding: '12px 16px', background: isReclamada ? '#fffbe6' : '#fff1f0', border: `1px solid ${isReclamada ? '#ffe58f' : '#ffccc7'}`, borderRadius: 8, marginTop: 8 }}>
                                <Text style={{ color: isReclamada ? '#d48806' : '#cf1322', fontWeight: 500 }}>
                                    {customMessage || defaultEmptyDesc}
                                </Text>
                            </div>
                        ) : (
                            <div>
                                <Text type="secondary">{loading ? 'Reclamando tus recompensas…' : 'Has reclamado el siguiente botín:'}</Text>
                                {hasUniqueItem && (
                                    <div style={{ marginTop: 10, padding: '6px 12px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, display: 'inline-block' }}>
                                        <Text strong style={{ color: '#d48806' }}>
                                            🌟 ¡Has conseguido una recompensa única!
                                        </Text>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '28px 0' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#f5222d' }} spin />} />
                            <Text type="secondary">Cargando recompensas...</Text>
                        </div>
                    ) : !isEmpty ? (
                        <List
                            itemLayout="horizontal"
                            dataSource={rewards}
                            renderItem={(item) => (
                                <List.Item style={{ justifyContent: 'center', borderBottom: 'none', padding: '8px 0' }}>
                                    <Space size="large" align="center" style={{ background: item.is_unique ? '#fffbf0' : '#f5f5f5', border: item.is_unique ? '1px solid #ffd591' : 'none', padding: '12px 24px', borderRadius: 12, width: '100%', justifyContent: 'flex-start' }}>
                                        {getIconForReward(item)}
                                        {getDescriptionForReward(item)}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    ) : null}
                </>
            )}
        </Modal>
    );
};

export default RecompensaSummaryModal;
